import React, { ChangeEvent, ReactNode, useState } from 'react';

import {
  CmBox,
  CmButton,
  CmFlex,
  CmFormErrorMessage,
  CmHelpterText,
  CmLabelAndInput,
  CmLabelAndSelect,
  CmLabelWithQuestionIconAndInput,
  CmLongLoading,
  CmPopover,
  CmScrollBox,
  CmStack,
} from '@/components/uiParts';
import { Prefecture, PREFECTURES } from '@/domain/models/Store';
import { AuthStoreFormError } from '@/pages/store-management/auth-stores/components/AuthStoreSidebarForm/validate';
import {
  authStoreForm,
  GeoLocation,
} from '@/pages/store-management/auth-stores/service';
import { ZipCodeResult } from '@/services/StoreManagement/PostCode/fetchAddressByPosCode';

import { ChangePasswordDialog } from './ChangePasswordDialog';

type Props = {
  authStoreId?: number;
  isOpenAddressList: boolean;
  name: string;
  email: string;
  phoneNumber: string;
  zipCode: string;
  prefecture: Prefecture | '';
  addresses: ZipCodeResult[];
  city: string;
  streetAddress: string;
  building: string;
  geoLocation?: GeoLocation;
  isLoading: boolean;
  error: AuthStoreFormError;
  children?: ReactNode;
  handleInputName: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputEmail: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputPhoneNumber: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputZipCode: (e: ChangeEvent<HTMLInputElement>) => void;
  onCloseAddressList: () => void;
  handleSearchAdressByPosCode: () => Promise<void>;
  handleSetAddress: (index: number) => void;
  handleInputCity: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangePrefecture: (e: ChangeEvent<HTMLSelectElement>) => void;
  handleInputStreetAddress: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputBuilding: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputLatitude?: (e: ChangeEvent<HTMLInputElement>) => void;
  handleInputLongitude?: (e: ChangeEvent<HTMLInputElement>) => void;
};
const PREFIX = 'auth_store-modal-';

export const StoreManagementAuthStoreForm: React.FC<Props> = ({
  authStoreId,
  isOpenAddressList,
  name,
  email,
  phoneNumber,
  zipCode,
  prefecture,
  addresses,
  city,
  streetAddress,
  building,
  geoLocation,
  isLoading,
  error,
  children,
  handleInputName,
  handleInputEmail,
  handleInputPhoneNumber,
  handleInputZipCode,
  onCloseAddressList,
  handleSearchAdressByPosCode,
  handleSetAddress,
  handleInputCity,
  handleChangePrefecture,
  handleInputStreetAddress,
  handleInputBuilding,
  handleInputLatitude,
  handleInputLongitude,
}) => {
  const [isChangePasswordModalOpen, setIsChangePasswordModalOpen] =
    useState(false);

  // children にはCreateAuthStoreModalから渡されるパスワード入力欄がはいるので、childrenが無い場合は編集フォームになる
  const isCreateForm = !!children;

  return (
    <CmBox>
      <CmStack spacing="5px">
        <CmLabelWithQuestionIconAndInput
          isRequired
          id={PREFIX + authStoreForm.name.key}
          isInvalid={error.name.isError}
          label={authStoreForm.name.label}
          fontWeight="bold"
          value={name}
          onChange={handleInputName}
          placeholder="海鮮居酒屋 日本橋堀留町店"
          question={authStoreForm.name.question}
        >
          <CmFormErrorMessage>{error.name.message}</CmFormErrorMessage>
        </CmLabelWithQuestionIconAndInput>
        <CmLabelWithQuestionIconAndInput
          isRequired
          id={PREFIX + authStoreForm.email.key}
          isInvalid={error.email.isError}
          label={authStoreForm.email.label}
          fontWeight="bold"
          value={email}
          onChange={handleInputEmail}
          placeholder="sample@gmail.com"
          question={authStoreForm.email.question}
        >
          <CmFormErrorMessage>{error.email.message}</CmFormErrorMessage>
        </CmLabelWithQuestionIconAndInput>
        {children}
        {!isCreateForm && (
          <CmFlex alignItems={'end'} width={'full'}>
            <CmLabelAndInput
              id={PREFIX + authStoreForm.password.key}
              label={authStoreForm.password.label}
              isRequired
              isInvalid={error.password.isError}
              fontWeight="bold"
              value="*************"
              disabled
            />
            {authStoreId && (
              <>
                <CmButton
                  onClick={() => setIsChangePasswordModalOpen(true)}
                  ml="10px"
                  variant={'outline'}
                >
                  変更
                </CmButton>
                <ChangePasswordDialog
                  id={`${PREFIX}${authStoreForm.password.key}${name}`}
                  key={`${PREFIX}${authStoreForm.password.key}${name}`}
                  authStoreId={authStoreId}
                  isModalOpen={isChangePasswordModalOpen}
                  setIsModalOpen={setIsChangePasswordModalOpen}
                />
              </>
            )}
          </CmFlex>
        )}
        <CmLabelAndInput
          isRequired
          id={PREFIX + authStoreForm.phoneNumber.key}
          isInvalid={error.phoneNumber.isError}
          label={authStoreForm.phoneNumber.label}
          fontWeight="bold"
          value={phoneNumber}
          onChange={handleInputPhoneNumber}
          placeholder="0311111111"
        >
          <CmHelpterText mt="3px">
            {authStoreForm.phoneNumber.label}
            はハイフン区切りなしで入力してください。
          </CmHelpterText>
          <CmFormErrorMessage>{error.phoneNumber.message}</CmFormErrorMessage>
        </CmLabelAndInput>
        <CmFlex width={'full'}>
          <CmLabelAndInput
            id={PREFIX + authStoreForm.zipCode.key}
            label={authStoreForm.zipCode.label}
            isRequired
            isInvalid={error.zipCode.isError}
            fontWeight="bold"
            value={zipCode}
            onChange={handleInputZipCode}
            placeholder="1030012"
          >
            <CmHelpterText mt="3px">
              {authStoreForm.zipCode.label}
              はハイフン区切りなしで入力してください。
            </CmHelpterText>
            <CmFormErrorMessage>{error.zipCode.message}</CmFormErrorMessage>
          </CmLabelAndInput>
          <CmPopover
            {...{
              isOpen: isOpenAddressList,
              onClose: onCloseAddressList,
              title: '住所選択',
              placement: 'left-end',
            }}
            button={
              // @ts-ignore TS2590
              <CmButton
                mt={'35px'}
                onClick={handleSearchAdressByPosCode}
                ml="10px"
                variant={'outline'}
              >
                検索
              </CmButton>
            }
          >
            <CmScrollBox height={'450px'}>
              {addresses.map((zipCode, index) => {
                const address =
                  zipCode.address1 + zipCode.address2 + zipCode.address3;
                return (
                  <CmButton
                    onClick={() => handleSetAddress(index)}
                    key={address}
                    mt={'10px'}
                    width={'100%'}
                    variant="outline"
                  >
                    {address}
                  </CmButton>
                );
              })}
            </CmScrollBox>
          </CmPopover>
        </CmFlex>
        <CmLabelAndSelect
          isRequired
          id={PREFIX + authStoreForm.prefecture.key}
          isInvalid={error.prefecture.isError}
          label={authStoreForm.prefecture.label}
          fontWeight="bold"
          value={prefecture}
          onChange={handleChangePrefecture}
          color={prefecture !== '' ? 'black' : '#696969'}
          error={
            <CmFormErrorMessage>{error.prefecture.message}</CmFormErrorMessage>
          }
        >
          <option hidden disabled value="">
            東京都
          </option>
          {PREFECTURES.map((prefecture) => (
            <option key={prefecture} value={prefecture}>
              {prefecture}
            </option>
          ))}
        </CmLabelAndSelect>
        <CmLabelAndInput
          isRequired
          id={PREFIX + authStoreForm.city.key}
          label={authStoreForm.city.label}
          isInvalid={error.city.isError}
          fontWeight="bold"
          value={city}
          onChange={handleInputCity}
          placeholder="中央区"
        >
          <CmFormErrorMessage>{error.city.message}</CmFormErrorMessage>
        </CmLabelAndInput>
        <CmLabelAndInput
          isRequired
          id={PREFIX + authStoreForm.streetAddress.key}
          label={authStoreForm.streetAddress.label}
          isInvalid={error.streetAddress.isError}
          fontWeight="bold"
          value={streetAddress}
          onChange={handleInputStreetAddress}
          placeholder="〇〇町1-1-1"
        >
          <CmFormErrorMessage>{error.streetAddress.message}</CmFormErrorMessage>
        </CmLabelAndInput>
        <CmLabelAndInput
          id={PREFIX + authStoreForm.building.key}
          label={authStoreForm.building.label}
          isInvalid={error.building.isError}
          fontWeight="bold"
          value={building}
          onChange={handleInputBuilding}
          placeholder="△△ビル101"
        >
          <CmFormErrorMessage>{error.building.message}</CmFormErrorMessage>
        </CmLabelAndInput>
        {geoLocation &&
          handleInputLatitude !== undefined &&
          handleInputLongitude !== undefined && (
            <>
              <CmLabelAndInput
                type="number"
                id={PREFIX + authStoreForm.latitude.key}
                label={authStoreForm.latitude.label}
                isInvalid={error.latitude.isError}
                fontWeight="bold"
                value={geoLocation.latitude || ''}
                onChange={handleInputLatitude}
                placeholder="10.0"
              >
                <CmFormErrorMessage>
                  {error.latitude.message}
                </CmFormErrorMessage>
              </CmLabelAndInput>
              <CmLabelAndInput
                type="number"
                id={PREFIX + authStoreForm.longitude.key}
                label={authStoreForm.longitude.label}
                isInvalid={error.longitude.isError}
                fontWeight="bold"
                value={geoLocation.longitude || ''}
                onChange={handleInputLongitude}
                placeholder="10.0"
              >
                <CmFormErrorMessage>
                  {error.longitude.message}
                </CmFormErrorMessage>
              </CmLabelAndInput>
            </>
          )}
      </CmStack>
      {isLoading && <CmLongLoading />}
    </CmBox>
  );
};
